import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface EmptyStateProps {
  /** Lucide icon component to render at the top. Defaults to `<Inbox>`. */
  icon?: LucideIcon;
  /** Title rendered below the icon. */
  title: ReactNode;
  /** Optional descriptive paragraph. */
  description?: ReactNode;
  /** Optional CTA (typically a `<Button>`). */
  action?: ReactNode;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

/**
 * Generic empty-state placeholder for lists with no items.
 *
 * Centered vertical stack with a circular muted icon, optional description
 * and an optional action slot. Lives in the layout wherever the consumer
 * places it — no fixed height enforced.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: EmptyStateProps): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-border ' +
          'border-dashed bg-bg-card/40 px-6 py-12 text-center',
        className,
      )}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-hover text-muted">
        <Icon size={24} aria-hidden />
      </span>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      {description ? (
        <p className="max-w-md text-sm text-muted">{description}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
